/**
 * Form07_PatientCareAuthorization.jsx
 * Collection: patient_care_authorizations
 * Redirect: /forms/08-health-history-questionnaire
 */
import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { FormShell, Field, SigBlock, saveToMongo, downloadPDF, useToast, C } from "../shared/formKit";

const API_URL = "/api/forms/patient-care-authorization";
const NEXT    = "/forms/08-health-history-questionnaire";

export default function PatientCareAuthorization({
  patientName="", patientAge="", patientGender="", practiceName="", onSubmit,
}) {
  const history = useHistory.push();
  const formRef  = useRef(null);
  const sigRef   = useRef(null);
  const [sigEmpty, setSigEmpty]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId]       = useState(null);
  const [showToast, Toast]          = useToast();

  const [form, setForm] = useState({
    authorizations: {
      payBenefits:    false,
      releaseInfo:    false,
      patientPicture: false,
      privacyNotice:  false,
      crispParticipation: false,
    },
    printName:        patientName,
    responsibleParty: patientName,
    relationship:     "Self",
    sigDate:          new Date().toISOString().split("T")[0],
  });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const setAuth = (k,v) => setForm(f=>({...f,authorizations:{...f.authorizations,[k]:v}}));
  const getSig = () => (!sigRef.current||sigRef.current.isEmpty()) ? null : sigRef.current.toDataURL("image/png");

  const buildDoc = () => ({
    formType:       "patient_care_authorization",
    patient:        { name:patientName, age:patientAge!==""?Number(patientAge):null, gender:patientGender },
    practiceName,
    authorizations: { ...form.authorizations },
    printName:      form.printName,
    authorization: {
      responsibleParty: form.responsibleParty,
      relationship:     form.relationship,
      sigDate:          form.sigDate||null,
      signatureDataURL: getSig(),
    },
    meta: { submittedAt:new Date().toISOString(), formVersion:"1.0.0" },
  });

  const handleSubmit = async () => {
    if (sigEmpty) { showToast("Please sign before submitting.","error"); return; }
    setSubmitting(true);
    try {
      const doc = buildDoc();
      const res = typeof onSubmit==="function" ? await onSubmit(doc) : await saveToMongo(doc, API_URL);
      if (res?._id) setSavedId(res._id);
      showToast("Saved! Redirecting…","success");
      setTimeout(()=>history.push(NEXT),1800);
    } catch(e){ console.error(e); showToast("Save failed.","error"); }
    finally { setSubmitting(false); }
  };

  const handleDownload = async () => {
    try { await downloadPDF(formRef,`patient-care-auth-${patientName||"patient"}.pdf`); showToast("Downloaded!"); }
    catch { showToast("PDF failed.","error"); }
  };

  const practice = practiceName || "_PRACTICE_NAME_";

  const authItems = [
    ["payBenefits","Authorization to Pay Benefits to Facility",
     `I hereby authorize payment directly to ${practice}. Not to exceed the reasonable and customary charge for those services. I understand that the provider's charge may exceed private insurance carrier payment, and if greater than such payment, I will be held responsible for that amount.`],
    ["releaseInfo","Authorization to Release Information",
     `I hereby authorize the treating provider to release any information required in the course of my examination or treatment to my insurance company, providers, individuals and entities authorized by me or their contracted entities. [If patient is a minor, parent must sign.] Please note that the use of all daily authorized and released records are not under ${practice} control.`],
    ["patientPicture","Authorization for Patient Picture",
     `I hereby authorize ${practice} to take a picture for my electronic medical records if I do not produce a current Photo ID.`],
    ["privacyNotice","Authorization for Receipt of Privacy Practices Notice",
     `I acknowledge receipt of Notice of Privacy Practices [Pages 1, 2, & 3] upon the present visit or within the last twelve calendar months.`],
    ["crispParticipation","Authorization to Participate in CRISP",
     `${practice} has chosen to participate in the Chesapeake Regional Information System for our patients, Inc. [CRISP], a statewide health information exchange. As permitted by law, your health information will be shared with this exchange to provide faster access and better coordination of care. You may "opt-out" by calling 1-877-952-7477 or visiting www.crisphealth.org.`],
  ];

  return (
    <FormShell title="Patient Care Authorization"
      subtitle={`Welcome to ${practice} — Please sign and return to front office staff.`}
      dbLabel="MongoDB · patient_care_authorizations"
      patientName={patientName} patientAge={patientAge} patientGender={patientGender}
      savedId={savedId} formRef={formRef}
      onSubmit={handleSubmit} onDownload={handleDownload} submitting={submitting}>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">!</span>Important Notice</div>
        <div className="wf-policy">
          <p>You are to provide copies of your current Insurance Card(s) &amp; Driver's License or Photo ID. We reserve the right to refuse treatment if these items are not provided. We also reserve the right to refuse treatment to those persons who use vulgarity or threats to staff or physicians.</p>
        </div>
      </div>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">2</span>Authorizations</div>
        {authItems.map(([key,heading,body])=>(
          <div key={key} style={{marginBottom:14,padding:"14px 16px",
            border:`1.5px solid ${form.authorizations[key]?C.teal:C.border}`,
            borderRadius:10, background:form.authorizations[key]?C.tealLt:C.white, transition:"all .2s"}}>
            <label style={{display:"flex",gap:12,cursor:"pointer",alignItems:"flex-start"}}>
              <input type="checkbox" style={{marginTop:3,accentColor:C.teal,flexShrink:0}}
                checked={form.authorizations[key]} onChange={e=>setAuth(key,e.target.checked)} />
              <div>
                <div style={{fontWeight:600,fontSize:14,marginBottom:4}}>• {heading}</div>
                <div style={{fontSize:13,color:C.muted,lineHeight:1.6}}>{body}</div>
              </div>
            </label>
          </div>
        ))}
      </div>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">3</span>Print Name</div>
        <Field label="Print Full Name">
          <input type="text" value={form.printName} onChange={e=>set("printName",e.target.value)} placeholder="Print your full name" />
        </Field>
      </div>

      <SigBlock sigRef={sigRef} sigEmpty={sigEmpty} setSigEmpty={setSigEmpty}
        responsibleParty={form.responsibleParty} setResponsibleParty={v=>set("responsibleParty",v)}
        relationship={form.relationship} setRelationship={v=>set("relationship",v)}
        sigDate={form.sigDate} setSigDate={v=>set("sigDate",v)} showRelationship={false} />

      {Toast}
    </FormShell>
  );
}

/*
── Mongoose Schema ────────────────────────────────────────────────────────────
const PatientCareAuthSchema = new Schema({
  formType:     { type:String, default:"patient_care_authorization" },
  patient:      { name:String, age:Number, gender:String },
  practiceName: String,
  authorizations: {
    payBenefits:Boolean, releaseInfo:Boolean,
    patientPicture:Boolean, privacyNotice:Boolean, crispParticipation:Boolean,
  },
  printName: String,
  authorization: { responsibleParty:String, relationship:String, sigDate:Date, signatureDataURL:String },
  meta: { submittedAt:Date, formVersion:String },
}, { timestamps:true, collection:"patient_care_authorizations" });
*/
